import { useEffect, useMemo, useRef, useState } from 'react';

import { Alert, AlertTitle, Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator, {
  HOME_INDICATOR_H,
} from '@/designs-dev/device/HomeIndicator';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import InviteSheet from '@/karehero-app/onboarding/InviteSheet';
import { type CircleMember } from '@/karehero-app/onboarding/Onboarding.content';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import ChatBubble from '@/karehero-app/tell-us-once/intake/ChatBubble';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import ChatTyping from '@/components/wrapped/ChatTyping';
import ChatActionRow from '@/components/wrapped/ChatActionRow';
import {
  DONE,
  INVITE,
  PROCESSING_BAR,
  SCRIPT,
  type TurnId,
} from '@/karehero-app/tell-us-once/intake/ProcessingChat.content';

/** How long each of the work's steps takes to tick, in the prototype. */
const STEP_MS = 2600;

/** How long Keni types before each message lands. */
const TYPING = 1200;
/** How long the opening sits finished before a replay starts it again. */
const REPLAY = 2600;

/**
 * How long into the screen the reading finishes, where nothing else settles it.
 *
 * A backstop rather than the plan: the notice normally waits for the
 * conversation to come to rest. This is for a screen nobody is answering, so it
 * does not sit on the last question forever.
 */
const DONE_AT = 3400;

type Message = { from: 'keni' | 'user'; text: string };

/**
 * Two bars, because the icon set has a play triangle and no pause.
 *
 * Drawn rather than added to llama: a pause is two rectangles, and this is
 * Loft's own chrome on a prototype rather than something the app ships.
 */
const PauseGlyph = () => (
  <Stack direction='row' spacing={0.5}>
    {[0, 1].map((i) => (
      <Box
        key={i}
        sx={{ width: 3, height: 11, borderRadius: '1px', bgcolor: 'inherit' }}
      />
    ))}
  </Stack>
);

/**
 * The end of the intake, still in the conversation.
 *
 * It was a centred orb and a status, which ends the conversation in order to
 * say the conversation is over. Saying it in the chat keeps them where they
 * have been all along, and leaves somewhere to ask the one question whose
 * answer does not depend on what comes back: who else helps with the caring.
 *
 * A new chat rather than the transcript. What they said is being read, and
 * scrolling back through it invites them to check our work before we have done
 * any.
 *
 * Answered by tapping, all the way through. The one answer that needs typing,
 * who to invite, goes in a sheet, so no composer comes and goes.
 */
/**
 * The answers already given on the way to a turn, so a screen can open partway
 * down a branch. Each step is a turn and the index of the reply taken from it.
 */
/** How long the invite sheet waits, once the question is asked, on the flows page. */
const INVITE_DELAY = 600;

const PATH: Record<
  'open' | 'invite' | 'email' | 'explain' | 'alone',
  { turn: TurnId; reply: number }[]
> = {
  open: [],
  invite: [{ turn: 'open', reply: 0 }],
  // "Yes, someone helps", then the invite question, with the sheet coming up
  // over it. "Invite to care circle" opens the sheet rather than being said.
  email: [{ turn: 'open', reply: 0 }],
  // "No, it's just me".
  alone: [{ turn: 'open', reply: 1 }],
  // "Yes, someone helps", then "What's a care circle?".
  explain: [
    { turn: 'open', reply: 0 },
    { turn: 'invite', reply: 1 },
  ],
};

const ProcessingChatScreen = ({
  account,
  /**
   * Replay the opening while nobody has answered it.
   *
   * On the flows page a mini nobody taps would otherwise show one frozen frame
   * of a thing that only exists as a movement. The first tap ends it: a loop
   * that restarts a conversation somebody is having takes it away from them.
   */
  loop = false,
  notice = false,
  noticeAfter = DONE_AT,
  from = 'open',
  onContinue,
  opening,
  done = DONE,
  work,
}: {
  account?: Account;
  loop?: boolean;
  /**
   * The reading comes back while they are still in here.
   *
   * The same screen rather than one of its own. The conversation carries on
   * underneath: a screen that ends it in order to announce something about it
   * takes the chat away at the moment it is answered.
   */
  notice?: boolean;
  /**
   * How long to wait for the reading before showing the notice anyway.
   *
   * The notice arrives when the conversation comes to rest, so on a screen
   * somebody is walking through it lands when they are done rather than over
   * the top of the question they are answering. This is the backstop for a
   * screen nobody touches, and the prototype sets it long: being demonstrated
   * means somebody is talking over it.
   */
  noticeAfter?: number;
  /**
   * Where the conversation is already up to.
   *
   * 'invite' opens with the two things said, "Yes, someone helps" answered and
   * the invite question coming. The notice screen starts there: showing the
   * opening a second time makes two adjacent steps the same screen, and the
   * follow-up only exists because they said somebody helps.
   */
  from?: keyof typeof PATH;
  /** The way on from the notice. Without one it is a card that says a thing. */
  onContinue?: () => void;
  /** Keni's first line, in place of the script's. Onboarding 1.1's is warmer. */
  opening?: string;
  /** What the notice says when the reading is done. */
  done?: typeof DONE;
  /**
   * Onboarding 1.1's notice area while the reading runs: the work, shown as
   * steps ticking off (3A in the warming-up doc), then what is being put
   * together once they have (3C). Gone when the notice comes up.
   */
  work?: {
    label: string;
    steps: string[];
    then: { label: string; items: string[]; note: string };
  };
}) => {
  // The script, with 1.1's opening line in place of the first one.
  const script = (turn: TurnId) =>
    turn === 'open' && opening
      ? { ...SCRIPT.open, says: [opening, ...SCRIPT.open.says.slice(1)] }
      : SCRIPT[turn];
  const [messages, setMessages] = useState<Message[]>(() =>
    PATH[from].flatMap(({ turn, reply }) => [
      ...script(turn).says.map((text) => ({ from: 'keni' as const, text })),
      { from: 'user' as const, text: script(turn).replies![reply].label },
    ]),
  );
  const [turn, setTurn] = useState<TurnId>(from === 'email' ? 'invite' : from);
  /** How many of this turn's lines have been said. */
  const [line, setLine] = useState(0);
  const [typing, setTyping] = useState(false);
  // Opened partway through, somebody has already answered: the replay is for a
  // conversation nobody has started.
  const [answered, setAnswered] = useState(from !== 'open');
  const [paused, setPaused] = useState(false);
  const [noticeUp, setNoticeUp] = useState(false);
  // How many of the work's steps have ticked. Timed here; in the app each one
  // ticks as its extraction event lands.
  const [ticked, setTicked] = useState(0);
  useEffect(() => {
    if (!work || ticked >= work.steps.length) return;
    const id = setTimeout(() => setTicked((n) => n + 1), STEP_MS);
    return () => clearTimeout(id);
  }, [work, ticked]);
  const [email, setEmail] = useState('');

  // Everyone invited so far, oldest first. Shown in the sheet as pending, so a
  // second invite starts from what has already gone.
  const [invited, setInvited] = useState<CircleMember[]>([]);
  const lastInvited = invited[invited.length - 1]?.name;
  // The confirmation names who it went to, so each one still says which
  // invite it was once there are a few in the thread.
  const current = useMemo(
    () =>
      turn === 'invited' && lastInvited
        ? { ...SCRIPT.invited, says: [INVITE.sent(lastInvited)] }
        : script(turn),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [turn, lastInvited],
  );
  const finished = line >= current.says.length;
  const replaying = loop && !answered;
  // The invite, open over the chat. Its own sheet rather than a composer: the
  // rest of this chat is answered by tapping, and a bar that turned up for one
  // answer and left again was the jank.
  const [inviting, setInviting] = useState(false);
  // On the flows page, the invite question is asked first and the sheet comes
  // up once it has been. Opened at once, the sheet covered it and it read as
  // "someone helps" jumping straight to an invite nobody had offered.
  useEffect(() => {
    if (from !== 'email') return;
    const id = setTimeout(() => setInviting(true), TYPING + INVITE_DELAY);
    return () => clearTimeout(id);
  }, [from]);

  // Keni types, then says, a line at a time. Driven off the line it is on
  // rather than a chain of timers, so a turn of any length plays itself.
  useEffect(() => {
    if (finished || paused) return;
    setTyping(true);
    const id = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { from: 'keni', text: current.says[line] },
      ]);
      setLine((l) => l + 1);
      setTyping(false);
    }, TYPING);
    return () => clearTimeout(id);
  }, [turn, line, finished, paused, current.says]);

  // The opening again, for a page nobody is tapping.
  useEffect(() => {
    if (!replaying || !finished || paused) return;
    const id = setTimeout(() => {
      setMessages([]);
      setLine(0);
    }, REPLAY);
    return () => clearTimeout(id);
  }, [replaying, finished, paused]);

  // Keep the foot of the conversation in view. Every turn adds height — the
  // dots, then a message, then the answers — and the one that needed scrolling
  // was the explanation, which says two things and then offers two more. What
  // is being asked and how to answer it should never be below the fold.
  const alert = useRef<HTMLDivElement | null>(null);
  const [alertH, setAlertH] = useState(0);
  useEffect(() => {
    setAlertH(alert.current?.offsetHeight ?? 0);
  }, [notice]);

  // What the transcript has to keep clear of. Both float over it, so without
  // this the question and its answers end up underneath whichever is up — and
  // the answers are the part you cannot afford to hide.
  const covered = (noticeUp ? alertH : 0) + (noticeUp ? 12 : 0);

  const scroller = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages.length, typing, finished, noticeUp]);

  // The conversation has nothing left to ask: the turn it is on offers no
  // answers and has finished saying its lines.
  const atRest = finished && !current.replies;

  useEffect(() => {
    if (!notice || noticeUp) return;
    // At rest it is not news any more, so it arrives promptly. Otherwise it
    // waits, and the wait is the caller's.
    const id = setTimeout(() => setNoticeUp(true), atRest ? 600 : noticeAfter);
    return () => clearTimeout(id);
  }, [notice, noticeUp, atRest, noticeAfter]);

  // Who they invited goes into the transcript as their answer, so the chat
  // shows they replied rather than a gap where the sheet was.
  const sendInvite = (member: CircleMember) => {
    setInviting(false);
    setInvited((prev) => [...prev, member]);
    setMessages((prev) => [...prev, { from: 'user', text: member.name }]);
    setTurn('invited');
    setLine(0);
  };

  // Closed without sending: nothing was said, so the rows are still there.
  const cancelInvite = () => setInviting(false);

  const pick = (reply: { label: string; to: TurnId }) => {
    setAnswered(true);
    // Opens the sheet and says nothing: the row is a control, not an answer.
    // The email they send is what goes in the thread.
    if (reply.to === 'email') {
      setInviting(true);
      return;
    }
    setMessages((prev) => [...prev, { from: 'user', text: reply.label }]);
    setTurn(reply.to);
    setLine(0);
  };

  return (
    // While it is replaying, the whole frame is the control: hovering says so,
    // and the badge stays up while it is held, which is the only thing on
    // screen that says paused rather than between messages.
    <Box
      onClick={replaying ? () => setPaused((p) => !p) : undefined}
      sx={{
        position: 'relative',
        height: '100%',
        cursor: replaying ? 'pointer' : 'default',
        '&:hover .loop-badge': { opacity: 1 },
      }}
    >
      {replaying && (
        <Box
          className='loop-badge'
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'custom.charcoal',
            color: 'common.white',
            display: 'grid',
            placeItems: 'center',
            opacity: paused ? 1 : 0,
            transition: 'opacity .15s ease',
          }}
        >
          {paused ? (
            <Icon icon={'play' as never} fontSize={12} />
          ) : (
            <PauseGlyph />
          )}
        </Box>
      )}

      <ScreenRoot relative>
        <StatusBar />
        {account && <AccountBar {...account} label={PROCESSING_BAR} />}

        <Box
          ref={scroller}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            px: 3,
            pt: 2,
            // Room for whatever is floating over it. See `covered`.
            pb: `${covered}px`,
          }}
        >
          <Stack spacing={1.5}>
            {/* Each message arrives rather than appears: a bubble that is
                simply there reads as history, and these are being said. */}
            {messages.map((m, i) => (
              <Arrive key={`${i}-${m.text}`}>
                <ChatBubble from={m.from} text={m.text} />
              </Arrive>
            ))}

            {/* Also once the conversation has nothing left to ask and the
                reading is still going: the dots say we are still working, until
                the notice that it is done takes their place. */}
            {(typing || (atRest && !noticeUp)) && (
              <Arrive>
                <ChatTyping />
              </Arrive>
            )}

            {/* The answers sit in the transcript under the question rather than
                in a bar at the foot of the screen. ChatActionRow is the app's
                own in-chat reply, so they read as answers to what was just
                asked instead of as the screen's controls.

                The wrapper swallows the click so answering does not also pause
                the replay it is ending. */}
            {finished && !typing && current.replies && (
              <Arrive>
                <Box onClick={(e) => e.stopPropagation()}>
                  <Stack spacing={1}>
                    {current.replies.map((reply) => (
                      <ChatActionRow
                        key={reply.label}
                        label={reply.label}
                        icon={reply.icon}
                        onClick={() => pick(reply)}
                      />
                    ))}
                  </Stack>
                </Box>
              </Arrive>
            )}
          </Stack>
        </Box>

        {/* The work, in the notice area while the reading runs. In the
            column rather than floating, so the transcript keeps clear of it
            without measuring it. */}
        {work && !noticeUp && (
          <Box sx={{ px: 3, pb: 1.5 }} onClick={(e) => e.stopPropagation()}>
            {ticked < work.steps.length ? (
              <FromUsCard
                label={work.label}
                items={work.steps}
                checked={ticked}
              />
            ) : (
              <FromUsCard label={work.then.label} note={work.then.note}>
                <Stack spacing={1}>
                  {work.then.items.map((item) => (
                    <Typography
                      key={item}
                      sx={{ color: 'text.primary' }}
                      variant='body2'
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </FromUsCard>
            )}
          </Box>
        )}

        {/* The notice floats above the safe area. Absolute, so nothing reserves height in the column while it is down:
            a flex child still holds its space when it is translated away, and
            the screen carried a band of empty page the whole time. */}
        {notice && (
          <Box
            ref={alert}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: HOME_INDICATOR_H,
              px: 3,
              pb: 1.5,
              transform: noticeUp
                ? 'translateY(0)'
                : `translateY(calc(100% + ${HOME_INDICATOR_H}px))`,
              opacity: noticeUp ? 1 : 0,
              transition:
                'transform .32s cubic-bezier(0.22, 1, 0.36, 1), bottom .28s cubic-bezier(0.22, 1, 0.36, 1), opacity .2s ease',
            }}
          >
            {/* A notice with the way on in it, not a message from Keni. Keni
                asks and is answered; this is the system saying a job finished,
                and dressing that as a bubble puts an action in the transcript
                with no way to answer it. */}
            <Alert severity='success' icon={false}>
              <AlertTitle>{done.title}</AlertTitle>
              <Stack spacing={2}>
                <Box>{done.body}</Box>
                <Button
                  variant='primary'
                  size='md'
                  isFullWidth
                  ariaLabel={done.action}
                  onPress={onContinue}
                >
                  {done.action}
                </Button>
              </Stack>
            </Alert>
          </Box>
        )}

        {/* The care circle's own invite sheet, so inviting from here looks
            and works the same as inviting from the circle. */}
        {inviting && (
          <InviteSheet
            invited={invited}
            onSend={sendInvite}
            onClose={cancelInvite}
          />
        )}

        <HomeIndicator />
      </ScreenRoot>
    </Box>
  );
};

/**
 * A message landing: up a little and into view, once, on mount.
 *
 * A flex column rather than a plain box, so it is transparent to alignment. A
 * bubble and the typing dots both place themselves with `alignSelf`, and inside
 * a block that does nothing: the dots stretched the width of the transcript and
 * a reply from the carer would have sat on the left.
 */
const Arrive = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      '@keyframes arrive': {
        from: { opacity: 0, transform: 'translateY(8px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
      animation: 'arrive .24s cubic-bezier(0.22, 1, 0.36, 1)',
    }}
  >
    {children}
  </Box>
);

export default ProcessingChatScreen;
